import React, { useCallback, useMemo, useContext, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { useSelector } from 'react-redux'
import SectionFormContainer from '../../../components/SectionFormContainer'
import GroomingAppointmentWizardContext from '../GroomingAppointmentWizardContext'
import { TextField, MenuItem } from '@material-ui/core'
import useGroomingVisit from '../../../../../hooks/useGroomingVisit'
import ActivityAlert from '../../../components/ActivityAlert'
import { FONT_WEIGHTS } from '../../../../../utils'
import { gray } from '../../../../../utils/colors'
import moment from 'moment'

const useStyles = makeStyles(() => ({
	selectContainer: { flex: 1, display: 'flex' },
	alertsContainer: {
		marginTop: '1rem'
	},
	availabilityText: {
		fontSize: '0.875rem',
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.Bold,
		color: gray[900]
	}
}))

const PreferredGroomer = (props) => {
	const {
		wizardContextProvider: { grooming_appointment, setWizardAttributeAndValues }
	} = useContext(GroomingAppointmentWizardContext)
	const { visit_attr, subtext = '', title = '' } = props // visit_attr = 'preferred_staff_id'
	const staffList = useSelector((state) => state.specific.team.staffList)
	const selectedFacility = useSelector((state) => state.specific.facilities.selected)
	const classes = useStyles()
	const { getAvailability, fetchingAvailability } = useGroomingVisit()

	// NOTE. For preferred groomer we are just storing the data in the root of wizard state as <preferred_staff_id>: availability
	// Example: '61a631580358b9001d1bbc0b': { shifts: [], duration, priceMin, priceMax, staff }
	// Therefore grooming_appointment[grooming_appointment[visit_attr]] would be looking for the grooming_appointment['61a631580358b9001d1bbc0b']
	// because grooming_appointment[visit_attr] = '61a631580358b9001d1bbc0b', equal to grooming_appointment['preferred_staff_id'] = '61a631580358b9001d1bbc0b'
	// This is probably a bit convoluted, but it follows the pattern of setting the visit_attr in the wizard config.

	// This will fetch availabilty for the selected groomer and store in wizard state
	// This will only run when the selected groomer changes and the shifts are not already stored in wizard state
	useEffect(() => {
		const fetchAvailability = async () => {
			const availability = await getAvailability(grooming_appointment)
			const newValues = { ...grooming_appointment }
			newValues[grooming_appointment[visit_attr]] = availability
			setWizardAttributeAndValues(newValues)
		}
		if (
			grooming_appointment[visit_attr] > '' &&
			grooming_appointment[visit_attr] !== 'none' &&
			grooming_appointment[grooming_appointment[visit_attr]] === undefined
		) {
			fetchAvailability(grooming_appointment)
		}
	}, [grooming_appointment, getAvailability, setWizardAttributeAndValues, visit_attr])

	const soonestAvailability = useMemo(() => {
		if (grooming_appointment[visit_attr] === '' || grooming_appointment[visit_attr] === 'none') {
			return ''
		}

		const availability = grooming_appointment[grooming_appointment[visit_attr]]
		const { shifts } = availability || { shifts: [] }

		if (shifts.length > 0) {
			const [firstShift] = shifts
			const { date } = firstShift
			const formatDate = moment(date).format('dddd, MMMM Do, YYYY')
			return `Soonest available date: ${formatDate}`
		} else {
			return 'No availability within the next 3 months'
		}
	}, [grooming_appointment])

	const options = useMemo(() => {
		const opts = [<MenuItem value="none">No Preference</MenuItem>]
		const groomers =
			staffList?.filter(
				(s) => s.active === true && (s?.position === 'Groomer' || s.position === 'Groom Tech') && s.market_id == selectedFacility.market_id
			) || []

		groomers.forEach((staff) => {
			opts.push(
				<MenuItem value={staff._id}>
					{staff.contact.last_name}, {staff.contact.first_name}
				</MenuItem>
			)
		})

		return opts
	}, [staffList])

	const handleSelection = useCallback(
		(e) => {
			const value = e.target.value
			const newValues = { ...grooming_appointment }
			newValues[visit_attr] = value
			newValues['month'] = -1
			setWizardAttributeAndValues(newValues)
		},
		[grooming_appointment, setWizardAttributeAndValues, visit_attr]
	)

	return (
		<SectionFormContainer
			title={title}
			subtext={subtext}
			titleStyles={{ flex: 0 }}
			childrenContainerStyles={{ flex: 0, marginBottom: '5rem' }}>
			<div className={classes.selectContainer}>
				<TextField
					select
					style={{
						minWidth: '400px',
						margin: 0
					}}
					label="Grooming Professional"
					variant="filled"
					value={grooming_appointment[visit_attr]}
					onChange={handleSelection}>
					{options}
				</TextField>
			</div>
			<div className={classes.alertsContainer}>
				{fetchingAvailability ? (
					<ActivityAlert text="Fetching soonest availability for that groomer" />
				) : null}
				{!fetchingAvailability && soonestAvailability > '' ? (
					<div className={classes.availabilityText}>{soonestAvailability}</div>
				) : null}
			</div>
		</SectionFormContainer>
	)
}

export default PreferredGroomer
